import { listTools, executeTool } from '@/services/ai/tools/ToolRegistry'

// Map ToolRegistry tools to a provided WebMCP instance
export function mapToolsToWebMCP(mcpInstance, options = {}) {
  if (!mcpInstance || typeof mcpInstance.registerTool !== 'function') return
  const confirmWrite = typeof options.confirmWrite === 'function' ? options.confirmWrite : null
  const allowWriteWithoutConfirm = options.allowWriteWithoutConfirm === true

  for (const t of listTools()) {
    mcpInstance.registerTool(
      t.name,
      t.description || '',
      t.schema || { type: 'object', properties: {} },
      async (args) => {
        if (t.kind === 'write') {
          if (!allowWriteWithoutConfirm) {
            if (!confirmWrite) {
              return {
                content: [
                  {
                    type: 'text',
                    text: JSON.stringify({
                      ok: false,
                      error: 'write_confirmation_required',
                      message: `Tool "${t.name}" requires explicit confirmation before execution.`
                    })
                  }
                ]
              }
            }

            const approved = await Promise.resolve(confirmWrite({ name: t.name, args: args || {} }))
            if (!approved) {
              return {
                content: [
                  {
                    type: 'text',
                    text: JSON.stringify({
                      ok: false,
                      error: 'write_cancelled',
                      message: `Tool "${t.name}" was cancelled.`
                    })
                  }
                ]
              }
            }
          }
        }

        const result = await executeTool(t.name, args)
        return { content: [{ type: 'text', text: JSON.stringify(result) }] }
      }
    )
  }
}

export default function initWebMcpIfEnabled() {
  // No-op default; call mapToolsToWebMCP(mcp) from an opt-in settings toggle or header button.
}

/**
 * Dynamically ensure the WebMCP script is loaded on the page.
 * Tries, in order: VITE_WEBMCP_URL, '/webmcp.js', '/js/webmcp.js'.
 * @returns {Promise<boolean>} true when window.WebMCP is available
 */
export async function ensureWebMCP() {
  try {
    if (typeof window !== 'undefined' && window.WebMCP) return true
    const candidates = []
    try {
      const envUrl = import.meta?.env?.VITE_WEBMCP_URL
      if (envUrl) {
        // env must point to a publicly-served script, not a source module path
        if (String(envUrl).includes('/src/')) {
          console.warn(
            '[WebMCP] VITE_WEBMCP_URL should point to a public URL (e.g. /js/webmcp.js), not a source path:',
            envUrl
          )
        } else {
          candidates.push(envUrl)
        }
      }
    } catch {
      /* ignore env access errors */
    }
    // Prefer /js/webmcp.js since many apps place it under public/js
    candidates.push('/js/webmcp.js')
    candidates.push('/webmcp.js')

    const tried = []
    for (const url of candidates.filter(Boolean)) {
      if (tried.includes(url)) continue
      tried.push(url)
      // Try as ES module first (if the build is ESM), then classic fallback
      const okModule = await injectScript(url, { type: 'module' })
      if (okModule && window.WebMCP) return true
      const okClassic = await injectScript(url, { type: 'classic' })
      if (okClassic && window.WebMCP) return true
    }
    console.warn('[WebMCP] Failed to load script from any candidate URL', tried)
    return false
  } catch {
    return false
  }
}

function injectScript(src, opts = {}) {
  return new Promise((resolve) => {
    try {
      const el = document.createElement('script')
      // cache-bust to avoid stale public file
      const ts = Date.now()
      const withTs = src.includes('?') ? `${src}&_ts=${ts}` : `${src}?_ts=${ts}`
      el.src = withTs
      el.async = true
      if (opts.type === 'module') el.type = 'module'
      el.onload = () => {
        try {
          console.info('[WebMCP] Loaded', src, 'as', el.type || 'classic')
        } catch {
          /* ignore logging errors */
        }
        resolve(true)
      }
      el.onerror = () => {
        try {
          console.warn('[WebMCP] Failed to load', src, 'as', el.type || 'classic')
        } catch {
          /* ignore logging errors */
        }
        resolve(false)
      }
      document.head.appendChild(el)
    } catch {
      resolve(false)
    }
  })
}
